(() => {
  if (window.BurdlifeCollectionCards) return;

  const cardSelector = '.boost-sd__product-item';
  const storageKey = 'burdlife:wishlist:v1';
  const densityStorageKey = 'burdlife:collection-density:v2';
  const densityOptions = ['3', '4', '5', '6'];
  const defaultDensity = '4';
  const productCache = new Map();
  const pendingProducts = new Map();
  const galleryState = new WeakMap();

  const icon = (name) => {
    const template = document.querySelector(`[data-bl-card-icon="${name}"]`);
    return template?.content?.firstElementChild?.cloneNode(true) || null;
  };
  const metalObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        metalObserver.unobserve(entry.target);
        const handle = productHandle(entry.target);
        if (handle) addMetalSwatches(entry.target, handle);
      });
    }, { rootMargin: '500px 0px' })
    : null;

  const readWishlist = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch (_error) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (_error) {
      return false;
    }

    window.dispatchEvent(new CustomEvent('burdlife:wishlist:change', {
      detail: { items },
    }));
    return true;
  };

  const productHandle = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    try {
      const url = new URL(link.href, window.location.origin);
      return url.pathname.split('/products/')[1]?.split('/')[0] || '';
    } catch (_error) {
      return '';
    }
  };

  const productDataFromCard = (card, handle) => ({
    id: String(card.dataset.productId || handle),
    handle,
    title: card.querySelector('.boost-sd__product-title')?.textContent?.trim() || '',
    price: card.querySelector('.boost-sd__product-price')?.textContent?.trim() || '',
    url: `/products/${handle}`,
    image: card.querySelector('.boost-sd__product-image-img--main')?.currentSrc
      || card.querySelector('.boost-sd__product-image-img--main')?.src
      || '',
  });

  const setWishlistState = (button, isSaved) => {
    button.setAttribute('aria-pressed', String(isSaved));
    const title = button.dataset.productTitle || 'product';
    button.setAttribute('aria-label', isSaved ? `Remove ${title} from wishlist` : `Save ${title} to wishlist`);
  };

  const syncWishlistButtons = (root = document) => {
    const savedIds = new Set(readWishlist().map((item) => String(item.id)));
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('.bl-card-wishlist').forEach((button) => {
      setWishlistState(button, savedIds.has(String(button.dataset.productId)));
    });
  };

  const syncNativeWishlistButtons = (root = document) => {
    const savedIds = new Set(readWishlist().map((item) => String(item.id)));
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('[data-bl-product-card-wishlist]').forEach((button) => {
      const card = button.closest('[data-bl-native-product-card]');
      if (!card) return;
      const isSaved = savedIds.has(String(card.dataset.blProductId));
      const title = card.dataset.blProductTitle || 'product';
      button.setAttribute('aria-pressed', String(isSaved));
      button.setAttribute('aria-label', `${isSaved ? 'Remove' : 'Save'} ${title} ${isSaved ? 'from' : 'to'} wishlist`);
    });
  };

  const readDensity = () => {
    try {
      const saved = window.localStorage.getItem(densityStorageKey);
      return densityOptions.includes(saved) ? saved : defaultDensity;
    } catch (_error) {
      return defaultDensity;
    }
  };

  const applyDensity = (columns, persist = false) => {
    const nextColumns = densityOptions.includes(String(columns)) ? String(columns) : defaultDensity;
    document.body.dataset.burdlifeGridColumns = nextColumns;

    document.querySelectorAll('.bl-grid-density__button').forEach((button) => {
      const isActive = button.dataset.columns === nextColumns;
      button.setAttribute('aria-pressed', String(isActive));
    });

    if (!persist) return;
    try {
      window.localStorage.setItem(densityStorageKey, nextColumns);
    } catch (_error) {
      return;
    }
  };

  const addDensityControl = () => {
    if (!document.body.matches('.template-collection, .template-search')) return;

    const toolbar = document.querySelector('.boost-sd__toolbar-content');
    if (!toolbar) return;

    const existingControl = toolbar.querySelector('.bl-grid-density');
    if (existingControl) {
      applyDensity(readDensity());
      return;
    }

    const control = document.createElement('div');
    control.className = 'bl-grid-density';
    control.setAttribute('role', 'group');
    control.setAttribute('aria-label', 'Products per row');

    const label = document.createElement('span');
    label.className = 'bl-grid-density__label';
    label.textContent = 'View';
    control.appendChild(label);

    densityOptions.forEach((columns) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bl-grid-density__button';
      button.dataset.columns = columns;
      button.setAttribute('aria-label', `Show ${columns} products per row`);
      button.title = `${columns} products per row`;

      const icon = document.createElement('span');
      icon.className = 'bl-grid-density__icon';
      icon.style.setProperty('--bl-density-columns', columns);
      icon.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < Number(columns); index += 1) {
        icon.appendChild(document.createElement('i'));
      }

      button.append(icon);
      button.addEventListener('click', () => applyDensity(columns, true));
      control.appendChild(button);
    });

    const sortingToolbar = toolbar.querySelector('.boost-sd__toolbar');
    toolbar.insertBefore(control, sortingToolbar || null);
    applyDensity(readDensity());
  };

  const placeCategoryNavigation = () => {
    if (!document.body.classList.contains('template-collection')) return;

    const navigation = document.querySelector('[data-bl-collection-category-nav]');
    const collectionHeader = document.querySelector('.boost-sd__header-main-2');
    if (!navigation || !collectionHeader) return;

    if (navigation.previousElementSibling !== collectionHeader) {
      collectionHeader.insertAdjacentElement('afterend', navigation);
    }
    navigation.hidden = false;
  };

  const compactMobileToolbar = () => {
    if (!document.body.matches('.template-collection, .template-search')) return;

    const toolbar = document.querySelector('.boost-sd__toolbar-content');
    if (!toolbar) return;

    const elements = Array.from(toolbar.querySelectorAll('*'));
    const refineText = elements.find((element) => element.children.length === 0
      && element.textContent.trim().toUpperCase() === 'REFINE BY');
    const refineControl = refineText?.closest('button') || refineText?.parentElement;
    refineControl?.classList.add('bl-mobile-refine-control');

    const countText = Array.from(document.querySelectorAll('.boost-sd-layout *')).find((element) => (
      element.children.length === 0 && /^\d+\s+products$/i.test(element.textContent.trim())
    ));
    (countText?.closest('.boost-sd__product-count') || countText?.parentElement)?.classList.add('bl-mobile-product-count');
  };

  const labelFilterControls = (root = document) => {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('.boost-sd__filter-tree-toggle-button').forEach((button) => {
      button.type = 'button';
      if (!button.getAttribute('aria-label') && !button.textContent.trim()) {
        button.setAttribute('aria-label', 'Filter products');
      }
    });
  };

  const addWishlistButton = (card, handle) => {
    const imageArea = card.querySelector('.boost-sd__product-item-grid-view-layout-image');
    const productInfo = card.querySelector('.boost-sd__product-info');
    if (!imageArea || !productInfo || card.querySelector('.bl-card-wishlist')) return;

    const product = productDataFromCard(card, handle);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bl-card-wishlist';
    button.dataset.productId = product.id;
    button.dataset.productTitle = product.title;
    const heartIcon = icon('heart');
    if (heartIcon) button.appendChild(heartIcon);

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const items = readWishlist();
      const itemIndex = items.findIndex((item) => String(item.id) === product.id);
      if (itemIndex >= 0) {
        items.splice(itemIndex, 1);
      } else {
        items.push(product);
      }

      if (writeWishlist(items)) syncWishlistButtons();
    });

    productInfo.appendChild(button);
    syncWishlistButtons(productInfo);
  };

  const labelNativeOptions = (root = document) => {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('[data-bl-native-product-card]').forEach((card) => {
      const holder = card.querySelector('.product-information .product-item__swatches__holder');
      const label = holder?.querySelector('.product-item__swatches__count span');
      if (!holder || !label || label.dataset.burdlifeOptionNames === 'true') return;

      const values = Array.from(holder.querySelectorAll('.swatch__link .visually-hidden'))
        .map((item) => item.textContent.trim())
        .filter(Boolean);
      const uniqueValues = [...new Set(values)];
      if (!uniqueValues.length) return;

      label.textContent = uniqueValues.join(', ');
      label.dataset.burdlifeOptionNames = 'true';
    });
  };

  const getProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);
    if (pendingProducts.has(handle)) return pendingProducts.get(handle);

    const request = window.fetch(`/products/${handle}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        productCache.set(handle, product);
        pendingProducts.delete(handle);
        return product;
      })
      .catch(() => {
        pendingProducts.delete(handle);
        return null;
      });

    pendingProducts.set(handle, request);
    return request;
  };

  const addZoomControl = (card, handle) => {
    const imageArea = card.querySelector('.boost-sd__product-item-grid-view-layout-image');
    if (!imageArea || imageArea.querySelector('.bl-card-zoom')) return;

    const title = card.querySelector('.boost-sd__product-title')?.textContent?.trim() || 'product';
    const link = document.createElement('a');
    link.className = 'bl-card-zoom';
    link.href = `/products/${handle}`;
    link.setAttribute('aria-label', `View ${title} details`);
    const searchIcon = icon('search');
    if (searchIcon) link.appendChild(searchIcon);
    imageArea.appendChild(link);
  };

  const galleryButton = (direction, title) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `bl-card-gallery__button bl-card-gallery__button--${direction}`;
    button.dataset.blCardGalleryButton = direction;
    button.setAttribute('aria-label', `${direction === 'previous' ? 'Previous' : 'Next'} image of ${title}`);
    const arrow = icon(direction);
    if (arrow) button.appendChild(arrow);
    return button;
  };

  const addGalleryControls = (card, product) => {
    if (!Array.isArray(product?.images) || product.images.length < 2) return;

    const imageArea = card.querySelector('.boost-sd__product-item-grid-view-layout-image');
    const imageHolder = card.querySelector('.boost-sd__product-image');
    if (!imageArea || !imageHolder || imageArea.hasAttribute('data-bl-card-gallery')) return;

    const title = product.title || card.querySelector('.boost-sd__product-title')?.textContent?.trim() || 'product';
    const stage = document.createElement('div');
    stage.className = 'bl-card-gallery__stage';
    stage.dataset.blCardGalleryStage = '';
    stage.setAttribute('aria-hidden', 'true');

    const controls = document.createElement('div');
    controls.className = 'bl-card-gallery__controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', `Browse images for ${title}`);
    controls.append(galleryButton('previous', title), galleryButton('next', title));

    imageArea.dataset.blCardGallery = '';
    imageArea.dataset.blCardGalleryHandle = product.handle;
    imageArea.dataset.blCardGalleryIndex = '0';
    imageArea.dataset.blCardGalleryTitle = title;
    imageHolder.appendChild(stage);
    imageArea.appendChild(controls);
    galleryState.set(imageArea, { images: product.images, index: 0, title });
  };

  const resizedImage = (source, width = 900) => {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return source;
    }
  };

  const showGalleryImage = async (gallery, direction) => {
    const handle = gallery.dataset.blCardGalleryHandle;
    let state = galleryState.get(gallery);

    if (!state && handle) {
      const product = await getProduct(handle);
      if (!product || !Array.isArray(product.images) || product.images.length < 2) {
        gallery.querySelector('.bl-card-gallery__controls')?.remove();
        return;
      }
      state = {
        images: product.images,
        index: Number.parseInt(gallery.dataset.blCardGalleryIndex || '0', 10),
        title: product.title || gallery.dataset.blCardGalleryTitle || 'product',
      };
      galleryState.set(gallery, state);
    }

    if (!state?.images?.length) return;
    const delta = direction === 'previous' ? -1 : 1;
    state.index = (state.index + delta + state.images.length) % state.images.length;
    gallery.dataset.blCardGalleryIndex = String(state.index);

    const stage = gallery.querySelector('[data-bl-card-gallery-stage]');
    if (!stage) return;
    const image = new Image();
    image.alt = `${state.title} view ${state.index + 1}`;
    image.decoding = 'async';
    image.src = resizedImage(state.images[state.index]);
    await image.decode().catch(() => {});
    stage.replaceChildren(image);
    stage.classList.add('is-visible');

    const status = gallery.querySelector('[data-bl-card-gallery-status]');
    if (status) status.textContent = `Image ${state.index + 1} of ${state.images.length}`;
  };

  const metalColor = (value) => {
    const name = String(value || '').toLowerCase();
    if (name.includes('rose')) return '#c9948c';
    if (name.includes('silver') || name.includes('rhodium') || name.includes('platinum')) return '#c7c8c9';
    if (name.includes('gold') || name.includes('vermeil')) return '#dfbd71';
    if (name.includes('black') || name.includes('onyx')) return '#353535';
    if (name.includes('pearl')) return '#eee9df';
    return '#d8d4d1';
  };

  const metalOptionFor = (product) => product?.options?.find((option) => (
    /(colo(u)?r|metal|finish|material)/i.test(option.name || '')
      && Array.isArray(option.values)
      && option.values.length > 1
  ));

  const variantForValue = (product, option, value) => {
    const optionKey = `option${option.position}`;
    return product.variants.find((variant) => variant[optionKey] === value && variant.available)
      || product.variants.find((variant) => variant[optionKey] === value)
      || null;
  };

  const selectMetal = (card, product, option, value, swatch) => {
    const variant = variantForValue(product, option, value);

    card.querySelectorAll('.bl-card-metal').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === swatch));
    });

    const label = card.querySelector('.bl-card-metals__label');
    if (label) label.textContent = value;

    if (!variant) return;
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.href = `/products/${product.handle}?variant=${variant.id}`;
    });
  };

  const addMetalSwatches = async (card, handle) => {
    const productInfo = card.querySelector('.boost-sd__product-info');
    if (!productInfo || productInfo.querySelector('.bl-card-metals')) return;

    const product = await getProduct(handle);
    if (!product || !card.isConnected) return;

    addGalleryControls(card, product);
    if (productInfo.querySelector('.bl-card-metals')) return;

    const option = metalOptionFor(product);
    if (!option) return;

    const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];
    const selectedValue = firstAvailable?.[`option${option.position}`] || option.values[0];
    const wrapper = document.createElement('div');
    wrapper.className = 'bl-card-metals';
    wrapper.setAttribute('aria-label', option.name);

    const options = document.createElement('div');
    options.className = 'bl-card-metals__options';

    option.values.forEach((value) => {
      const variant = variantForValue(product, option, value);
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'bl-card-metal';
      swatch.style.setProperty('--bl-metal-color', metalColor(value));
      swatch.dataset.variantId = variant?.id || '';
      swatch.setAttribute('aria-label', `${option.name}: ${value}`);
      swatch.setAttribute('aria-pressed', String(value === selectedValue));
      swatch.title = value;
      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectMetal(card, product, option, value, swatch);
      });
      options.appendChild(swatch);
    });

    const label = document.createElement('span');
    label.className = 'bl-card-metals__label';
    label.textContent = selectedValue;
    wrapper.append(options, label);
    productInfo.appendChild(wrapper);
  };

  const modernizeQuickAdd = (card) => {
    const title = card.querySelector('.boost-sd__product-title')?.textContent?.trim() || 'product';
    card.querySelectorAll('.boost-sd__product-image-row--bottom .boost-sd__button').forEach((button) => {
      const currentLabel = button.dataset.burdlifeOriginalLabel || button.getAttribute('aria-label') || button.textContent.trim() || 'Choose product';
      if (!button.dataset.burdlifeOriginalLabel) button.dataset.burdlifeOriginalLabel = currentLabel;
      const addsDirectly = currentLabel.toLowerCase().includes('add');
      button.setAttribute('aria-label', addsDirectly ? `Add ${title} to cart` : `View options for ${title}`);
      button.title = addsDirectly ? `Add ${title}` : `View options for ${title}`;
      button.classList.toggle('bl-card-action--add', addsDirectly);
      button.classList.toggle('bl-card-action--options', !addsDirectly);

      if (!button.querySelector('.bl-card-action__icon')) {
        const wrapper = document.createElement('span');
        wrapper.className = 'bl-card-action__icon';
        const actionIcon = icon('plus');
        if (actionIcon) wrapper.appendChild(actionIcon);
        button.appendChild(wrapper);
      }

      if (!addsDirectly && !button.dataset.burdlifeOptionHandler) {
        button.dataset.burdlifeOptionHandler = 'true';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          const productLink = card.querySelector('a[href*="/products/"]');
          if (productLink?.href) window.location.assign(productLink.href);
        }, true);
      }
    });
  };

  const placeQuickAddOnImage = (card) => {
    const imageArea = card.querySelector('.boost-sd__product-item-grid-view-layout-image');
    const quickAddColumn = card.querySelector('.boost-sd__product-image-column--in-bottom');
    if (!imageArea || !quickAddColumn) return;

    quickAddColumn.classList.add('bl-card-quick-add', 'bl-card-action');
    if (quickAddColumn.parentElement !== imageArea) imageArea.appendChild(quickAddColumn);
  };

  const enhanceCard = (card) => {
    if (!(card instanceof Element)) return;
    const handle = productHandle(card);
    if (!handle) return;

    modernizeQuickAdd(card);
    placeQuickAddOnImage(card);
    addWishlistButton(card, handle);
    addZoomControl(card, handle);
    if (metalObserver) {
      metalObserver.observe(card);
    } else {
      addMetalSwatches(card, handle);
    }
    card.dataset.burdlifeCardEnhanced = 'true';
  };

  const enhanceAll = (root = document) => {
    placeCategoryNavigation();
    addDensityControl();
    compactMobileToolbar();
    labelFilterControls(root);
    labelNativeOptions(root);
    if (root.matches?.(cardSelector)) enhanceCard(root);
    root.querySelectorAll?.(cardSelector).forEach(enhanceCard);
  };

  let scheduled = false;
  const scheduleEnhancement = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      enhanceAll();
    });
  };

  const observer = new MutationObserver(scheduleEnhancement);

  const init = () => {
    enhanceAll();
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('storage', () => {
      syncWishlistButtons();
      syncNativeWishlistButtons();
    });
    window.addEventListener('burdlife:wishlist:change', () => {
      syncWishlistButtons();
      syncNativeWishlistButtons();
    });
    document.addEventListener('boost-sd:filter-result-loaded', scheduleEnhancement);
    document.addEventListener('boost-sd:product-list-updated', scheduleEnhancement);
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-bl-card-gallery-button]');
      if (!button) return;
      const gallery = button.closest('[data-bl-card-gallery]');
      if (!gallery) return;
      event.preventDefault();
      event.stopPropagation();
      showGalleryImage(gallery, button.dataset.blCardGalleryButton);
    }, true);

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-bl-product-card-wishlist]');
      if (!button) return;
      const card = button.closest('[data-bl-native-product-card]');
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();

      const product = {
        id: String(card.dataset.blProductId || card.dataset.blProductHandle),
        handle: card.dataset.blProductHandle,
        title: card.dataset.blProductTitle,
        price: card.dataset.blProductPrice,
        url: card.dataset.blProductUrl,
        image: card.dataset.blProductImage,
      };
      const items = readWishlist();
      const itemIndex = items.findIndex((item) => String(item.id) === product.id);
      if (itemIndex >= 0) items.splice(itemIndex, 1);
      else items.push(product);

      if (writeWishlist(items)) syncNativeWishlistButtons();
    }, true);

    syncNativeWishlistButtons();
    labelNativeOptions();
  };

  window.BurdlifeCollectionCards = {
    refresh: enhanceAll,
    wishlist: readWishlist,
    setDensity: (columns) => applyDensity(columns, true),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
